import { useState, useEffect, useCallback } from 'react';

import * as fp from 'lodash/fp';

import { AlgoliaItem } from '~/types/store/item';
import { FetchingDataStatus } from '~/types/utilities';

import { itemIndex } from '~/algolia/init';

import { PLATFORM_ID } from '~/globalVariables';
import { useRecoilValue } from 'recoil';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import isInOperatingTime from '~/utils/isInOperatingTime';
import sortByDisabledStatus from '~/utils/sortByDisabledStatus';

const formatData = (d: Array<any>, pickMealAt: Date | null) => {
  return d
    .filter((hit) => hit)
    .map((item) => ({
      ...item,
      disabled: !isInOperatingTime(item.preOrderSchedule, pickMealAt),
    }));
};

export default function useSearchItem({
  searchText,
  storeIds,
  tags,
  itemLimit,
}: {
  searchText: string;
  storeIds: Array<string>;
  tags: Array<string>;
  itemLimit: number;
}) {
  const [fetchingStatus, setFetchingStatus] = useState<FetchingDataStatus>(FetchingDataStatus.INIT);
  const [searchItems, setSearchItems] = useState<Array<AlgoliaItem>>([]);
  const [searchItemCount, setSearchItemCount] = useState<number>(0);
  const [nextPage, setNextPage] = useState<number>(0);
  const pickMealAt = useRecoilValue(pickMealAtAtom);

  const handleGetItems = useCallback(
    async ({
      storeIds,
      tags,
      searchText,
      itemLimit,
      init,
      nextPage,
    }: {
      storeIds: Array<string>;
      tags: Array<string>;
      searchText: string;
      itemLimit: number;
      nextPage: number;
      init: boolean;
    }) => {
      if (!searchText && !storeIds.length && !tags.length) {
        setFetchingStatus(FetchingDataStatus.INIT);
        setSearchItems([]);
        setSearchItemCount(0);
        setNextPage(0);
        return;
      }
      try {
        setFetchingStatus(FetchingDataStatus.LOADING);

        const res = await itemIndex.search<AlgoliaItem>(searchText, {
          filters: `isAvailable:true${
            fp.gt(storeIds.length, 0)
              ? ` AND ( ${storeIds.map((id) => `storeId:${id}`).join(' OR ')} )`
              : ''
          }${
            fp.gt(tags.length, 0)
              ? ` AND ( ${tags.map((tag) => `tags.${PLATFORM_ID}:${tag}`).join(' OR ')} )`
              : ''
          }`,
          page: init ? 0 : nextPage,
          hitsPerPage: itemLimit,
        });
        init && setSearchItemCount(res.nbHits);
        setNextPage(res.page + 1);
        setSearchItems((prev) =>
          sortByDisabledStatus(
            init
              ? formatData(res.hits, pickMealAt)
              : fp.uniqBy<AlgoliaItem>('objectID')([...prev, ...formatData(res.hits, pickMealAt)]),
          ),
        );
        setFetchingStatus(FetchingDataStatus.SUCCESS);
      } catch (err) {
        setSearchItemCount(0);
        setNextPage(0);
        setSearchItems([]);
        setFetchingStatus(FetchingDataStatus.FAIL);
        console.error('algolia get items error', err);
      }
    },
    [pickMealAt],
  );

  const handleLoadNextPage = () => {
    if (fetchingStatus === FetchingDataStatus.LOADING) {
      return;
    }
    handleGetItems({ storeIds, tags, searchText, itemLimit, nextPage, init: false });
  };

  useEffect(() => {
    handleGetItems({ storeIds, tags, searchText, itemLimit, nextPage: 0, init: true });
  }, [storeIds, searchText, tags, itemLimit, handleGetItems]);

  return { fetchingStatus, searchItems, searchItemCount, handleLoadNextPage };
}
